// angular
import { Injectable } from '@angular/core';

// addons
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

// models
import { ILoginResponse, IUser, IUserLogin, IVerifyRequest, IVerifyResponse, LoginResult, VerifyResult } from '@shared/models';

// services
import { ApiService } from './api.service';
import { CacheService } from './cache.service';

@Injectable()
export class AuthService {
  url = '/auth';

  constructor(
    private apiService: ApiService,
    private cacheService: CacheService
  ) {}

  get token() {
    return this.cacheService.getCacheSlice('accessToken');
  }

  login({ userName, password }: IUserLogin): Observable<ILoginResponse> {
    return this.apiService.post2(`${this.url}/login`, { userName, password }).pipe(
      map((result: ILoginResponse) => {
        if (!!result && result.result === LoginResult.SUCCESS) {
          this.setUserToCache(result.token);
        }
        return result;
      })
    );
  }

  verifyMfa(request: IVerifyRequest): Observable<IVerifyResponse> {
    return this.apiService.post2(`${this.url}/verify`, { id: request.id, code: request.code }).pipe(
      map((result: IVerifyResponse) => {
        if (!!result && result.result === VerifyResult.SUCCESS) {
          this.setUserToCache(result.token);
        }
        return result;
      })
    );
  }

  logout() {
    return this.cacheService.clearCache();
  }

  chechUserCache(): Observable<any> {
    return this.cacheService.chechCache([
      'accessToken',
      'firstName',
      'lastName',
      'initials',
      'role',
      'organizationName',
      'organizationStatus',
      'email',
      'organization.name',
      'organization.address',
      'organization.city',
      'organization.region',
      'organization.postalCode',
      'organization.coc',
      'organization.iban',
      'organization.mobilePhone',
      'organization.annualStaffTurnover',
      'organization.initialNumberOfCertificates',
      'organization.initialPercentageOfShares',
    ]);
  }

  private setUserToCache(user: IUser) {
    const {
      accessToken,
      firstName,
      lastName,
      initials,
      role,
      organizationName,
      organizationStatus,
      email
    } = user;

    this.cacheService.setToCache({ key: 'accessToken', value: accessToken });
    this.cacheService.setToCache({ key: 'firstName', value: firstName });
    this.cacheService.setToCache({ key: 'lastName', value: lastName });
    this.cacheService.setToCache({ key: 'initials', value: initials });
    this.cacheService.setToCache({ key: 'role', value: role });
    this.cacheService.setToCache({ key: 'organizationName', value: organizationName });
    this.cacheService.setToCache({ key: 'organizationStatus', value: organizationStatus });
    this.cacheService.setToCache({ key: 'email', value: email });

    if (!! user.organization) {
      this.cacheService.setToCache({key: 'organization.name', value: user.organization.name});
      this.cacheService.setToCache({key: 'organization.address', value: user.organization.address});
      this.cacheService.setToCache({key: 'organization.city', value: user.organization.city});
      this.cacheService.setToCache({key: 'organization.region', value: user.organization.region});
      this.cacheService.setToCache({key: 'organization.postalCode', value: user.organization.postalCode});
      this.cacheService.setToCache({key: 'organization.coc', value: user.organization.coc});
      this.cacheService.setToCache({key: 'organization.iban', value: user.organization.iban});
      this.cacheService.setToCache({key: 'organization.mobilePhone', value: user.organization.mobilePhone});
      this.cacheService.setToCache({key: 'organization.annualStaffTurnover', value: user.organization.annualStaffTurnover});
      this.cacheService.setToCache({key: 'organization.initialNumberOfCertificates', value: user.organization.initialNumberOfCertificates});
      this.cacheService.setToCache({key: 'organization.initialPercentageOfShares', value: user.organization.initialPercentageOfShares});
    }
  }
}
